import { HTMLAttributes, forwardRef, useState } from 'react';

import {
  Theme,
  WithStyles,
  createStyles,
  emphasize,
  withStyles,
} from '@material-ui/core';

import classNames from 'classnames';

import { Avatar, Button } from '@backstage/ui';

import { InputGroup } from '@spotify/confidence-core-react';
import { getTypeOrNull } from '@spotify/confidence-graphql';
import {
  TextareaWithMentions,
  replaceEmailsWithMentions,
} from '@spotify/plugin-confidence-comments-react';
import { useUserSuggestions } from '@spotify/plugin-confidence-comments-react';
import { useCurrentUser } from '@spotify/plugin-confidence-iam-react';

const styles = (theme: Theme) =>
  createStyles({
    root: {
      marginTop: theme.spacing(1.5),
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: theme.spacing(1),
    },
    inputGroup: {
      width: '100%',
      boxShadow: 'none',
      padding: theme.spacing(2),
      background: theme.palette.action.active,
      borderRadius: theme.shape.borderRadius,
      '&:focus': {
        background: emphasize(theme.palette.action.active, 0.02),
      },
    },
    container: {
      flex: 1,
      width: '100%',
      display: 'flex',
      flexDirection: 'row',
      gap: theme.spacing(1),
    },
  });

export interface CommentInputProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'onSubmit'>,
    WithStyles<typeof styles> {
  onSubmit?: (comment: string) => void | Promise<void>;
  placeholder?: string;
  autoFocus?: boolean;
}

const CommentInput = forwardRef<HTMLDivElement, CommentInputProps>(
  (props, ref) => {
    const {
      className,
      classes,
      onSubmit,
      placeholder = 'Leave a comment',
      autoFocus,
      ...rest
    } = props;

    const { currentUser } = useCurrentUser();
    const avatar = currentUser?.user?.pictureUri ?? undefined;
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [value, setValue] = useState<string>('');
    const [mentionQuery, setMentionQuery] = useState<string | undefined>();

    const { users, suggestions } = useUserSuggestions(mentionQuery);

    const handleSubmit = async () => {
      if (!value.trim()) return;

      const pendingValue = value;
      setValue('');

      setIsSubmitting(true);
      try {
        if (onSubmit) {
          await onSubmit(replaceEmailsWithMentions(value, users));
          setValue('');
        }
      } catch (error) {
        setValue(pendingValue);
        throw error;
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <div ref={ref} className={classNames(classes.root, className)} {...rest}>
        <div className={classes.container}>
          <Avatar
            src={avatar ?? ''}
            name={getTypeOrNull(currentUser?.identity)?.displayName ?? ''}
          />
          <InputGroup.Root className={classes.inputGroup}>
            <TextareaWithMentions
              minRows={4}
              maxRows={10}
              value={value}
              onValueChange={setValue}
              onSubmit={handleSubmit}
              placeholder={placeholder}
              disabled={isSubmitting}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus={autoFocus}
              suggestions={suggestions}
              onMentionSearch={setMentionQuery}
            />
          </InputGroup.Root>
        </div>
        <Button size="small" variant="secondary" onPress={handleSubmit}>
          Post comment
        </Button>
      </div>
    );
  },
);

CommentInput.displayName = 'ActivityFeedItem.CommentInput';

export default withStyles(styles)(CommentInput);
